import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useDebounce } from "use-debounce";
import { FilterProps } from "../types/AppTypes";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import { useNotification } from "./notification.hook";
import PotentialMatrixDevelopmentCalibrationService from "../services/api/potentialMatrix/PotentialMatriDevelopmentCalibration.service";
import PotentialMatrixCollaboratorsService from "../services/api/potentialMatrix/PotentialMatrixCollaborators.service";
import { RefreshReactQuery } from "../constants/AppConstants";
import { QueryParam } from "../types/api/generic.type";

interface useCmFollowListProps {
  cmId: number | undefined;
  filters: FilterProps[];
  enabled: boolean;
}

export const useCmFollowList = ({
  cmId,
  filters,
  enabled,
}: useCmFollowListProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, refetch, isError } = useQuery({
    queryKey: ["get-calibration-career-managers-follow", cmId, filters],
    queryFn: () =>
      PotentialMatrixDevelopmentCalibrationService.getSpeakerFollowList(
        cmId!,
        filters,
      ),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: enabled && !!cmId,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching, refetch };
};

export const useUpdateCalibration = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      collaboratorId,
      filters,
    }: {
      collaboratorId: number;
      filters: FilterProps[];
    }) =>
      PotentialMatrixDevelopmentCalibrationService.update(
        collaboratorId,
        filters,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-potential-matrix"] });
      queryClient.invalidateQueries({
        queryKey: ["get-calibration-cm-list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["get-calibration-career-managers-follow"],
      });
      queryClient.invalidateQueries({
        queryKey: ["get-infinite-examined-collaborators"],
      });
      queryClient.invalidateQueries({
        queryKey: ["get-potential-matrix-stats"],
      });
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

interface useCmInfiniteListProps {
  pageSize: number;
  filters: QueryParam[];
  enabled: boolean;
}

export const useCmInfiniteList = ({
  pageSize,
  filters,
  enabled,
}: useCmInfiniteListProps) => {
  const { notification } = useNotification();
  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    refetch,
    isError,
  } = useInfiniteQuery({
    queryKey: ["get-calibration-cm-list", filters],
    queryFn: ({ pageParam }) =>
      PotentialMatrixDevelopmentCalibrationService.getSpeakersList(
        pageSize,
        pageParam as number,
        filters,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length * pageSize < lastPage.pagination.total
        ? allPages.length
        : undefined,
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );
  const pagination = data?.pages[data.pages.length - 1]?.pagination ?? null;

  const initLoading = isLoading;
  const fetchingMore = isFetching && (data?.pages.length ?? 0) > 0;

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    data: items,
    initLoading,
    fetchingMore,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    refetch,
    pagination,
  };
};

interface useExaminedCollaboratorsInfiniteProps {
  pageSize: number;
  queryParams: QueryParam[];
  enabled?: boolean;
  debounce?: number;
}

export const useExaminedCollaboratorsInfinite = ({
  pageSize,
  queryParams,
  enabled = true,
  debounce: debounceMs = 500,
}: useExaminedCollaboratorsInfiniteProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, debounceMs);

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    refetch,
    isError,
  } = useInfiniteQuery({
    queryKey: [
      "get-infinite-examined-collaborators",
      debouncedSearch,
      queryParams,
    ],
    queryFn: ({ pageParam, signal }) => {
      const params = [
        ...queryParams,
        ...(debouncedSearch
          ? [{ key: "search", value: [debouncedSearch] }]
          : []),
      ];
      return PotentialMatrixCollaboratorsService.list(
        pageSize,
        pageParam as number,
        params,
        signal,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length * pageSize < lastPage.pagination.total
        ? allPages.length
        : undefined,
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );
  const pagination = data?.pages[data.pages.length - 1]?.pagination ?? null;

  const initLoading = isLoading;
  const fetchingMore = isFetching && (data?.pages.length ?? 0) > 0;

  const clearSearch = useCallback(() => setSearch(""), []);

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    data: items,
    initLoading,
    fetchingMore,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    refetch,
    pagination,
    search,
    setSearch,
    clearSearch,
    page: (data?.pages.length ?? 1) - 1,
  };
};
